import React, { useEffect, useState } from 'react';
import { 
    makeStyles, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions, 
    Button 
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ChargeAppLogo from '../../static/chargesk.webp';

const useStyles = makeStyles((theme) => ({
    // Presný pôvodný štýl pre lištu
    appBanner: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0',
        position: 'relative',
        gap: '12px',
        width: '100%',
        boxSizing: 'border-box',
    },
    appIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        objectFit: 'cover'
    },
    textContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '& strong': {
            fontSize: '14px',
            color: '#000',
        },
        '& span': {
            fontSize: '12px',
            color: '#666',
        }
    },
    actionBtn: {
        backgroundColor: '#000',
        color: '#fff !important',
        padding: '8px 14px',
        borderRadius: '4px',
        textDecoration: 'none !important',
        fontSize: '12px',
        fontWeight: 'bold',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        border: 'none'
    },

    // Ladiace štýly pre Dialog
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        paddingTop: '20px',
    },
    dialogAppIcon: {
        width: '64px',
        height: '64px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        objectFit: 'cover',
        marginBottom: '16px',
    },
    dialogTitle: {
        textAlign: 'center',
        fontWeight: 'bold',
        color: '#000',
    },
    dialogActions: {
        flexDirection: 'column',
        gap: '8px',
        padding: '16px 24px 24px',
    },
    dialogOpenBtn: {
        backgroundColor: '#000',
        color: '#fff',
        fontWeight: 'bold',
        borderRadius: '4px',
        textTransform: 'none',
        fontSize: '14px',
        padding: '10px 16px',
        '&:hover': {
            backgroundColor: '#333',
        },
    },
}));

const AppBanner = () => {
    const classes = useStyles();
    const location = useLocation();
    const { t } = useTranslation();

    const [bannerVisible, setBannerVisible] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const isAuthenticated = useSelector((state: any) => state.user?.login?.value?.isLoggedIn);

    useEffect(() => {
        // 1. LIŠTA: Zobrazuje sa LEN NEPRIHLÁSENÝM na allowedPaths
        const allowedPaths = ['/', '/login', '/register'];
        const isAllowedPath = allowedPaths.includes(location.pathname);

        if (!isAuthenticated && isAllowedPath) {
            setBannerVisible(true);
        } else {
            setBannerVisible(false);
        }

        // 2. DIALOG: Zobrazuje sa 1-KRÁT ZA DEŇ (všetkým)
        const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const lastShownDate = localStorage.getItem('appDialogLastShownDate');

        if (lastShownDate !== todayStr) {
            setDialogVisible(true);
        }
    }, [location.pathname, isAuthenticated]);

    const handleCloseDialog = () => {
        setDialogVisible(false);
        
        // Uloženie dnešného dátumu -> znovu sa ukáže až zajtra
        const todayStr = new Date().toISOString().split('T')[0];
        localStorage.setItem('appDialogLastShownDate', todayStr);
    };

    const isIOS = () => {
        return (
            /(iPad|iPhone|iPod)/g.test(navigator.userAgent) ||
            (navigator.userAgent.includes("Mac") && "ontouchend" in document)
        );
    };

    const handleOpenApp = (e: any) => {
        e.preventDefault();

        const storeUrl = isIOS()
            ? "https://apps.apple.com/sk/app/charge-sk/id6745960330"
            : "https://play.google.com/store/apps/details?id=eu.starbug.charge.sk";

        if (isIOS()) {
            window.location.href = "charge://";
            setTimeout(() => {
                if (document.visibilityState === 'visible') {
                    window.location.href = storeUrl;
                }
            }, 1500);
            return;
        }

        // Android
        const intentUrl = `intent://open#Intent;scheme=chargesk;package=eu.starbug.charge.sk;S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`;
        window.location.href = intentUrl;
    };

    return (
        <>
            {/* 1. HORNÁ LIŠTA (Len neprihlásení) */}
            {bannerVisible && (
                <div className={classes.appBanner}>
                    <img src={ChargeAppLogo} alt="App Icon" className={classes.appIcon} />

                    <div className={classes.textContainer}>
                        <strong>Charge.sk</strong>
                        <span>{t('common.shortNotice') || 'Prejsť do aplikácie'}</span>
                    </div>

                    <button className={classes.actionBtn} onClick={handleOpenApp}>
                        {t('common.open') || 'OTVORIŤ'}
                    </button>
                </div>
            )}

            {/* 2. DIALOG (1x denné prihlásenému aj neprihlásenému) */}
            <Dialog
                open={dialogVisible}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, backgroundColor: '#ffffff' }
                }}
            >
                <DialogContent className={classes.dialogContent}>
                    <img src={ChargeAppLogo} alt="Charge.sk App" className={classes.dialogAppIcon} />
                    <DialogTitle className={classes.dialogTitle} disableTypography>
                        <h2 style={{ margin: 0, fontSize: '20px' }}>Charge.sk</h2>
                    </DialogTitle>
                    <DialogContentText style={{ color: '#666', marginTop: 8, fontSize: '14px' }}>
                        {t('common.dialogNotice') || 'Webová verzia bude čoskoro vypnutá. Prejdite na mobilnú aplikáciu pre lepší zážitok a nové funkcie.'}
                    </DialogContentText>
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        className={classes.dialogOpenBtn}
                        onClick={handleOpenApp}
                    >
                        {t('common.openInApp') || 'STIAHNUŤ APLIKÁCIU'}
                    </Button>
                    
                    <Button 
                        onClick={handleCloseDialog} 
                        color="default" 
                        fullWidth
                        style={{ color: '#888', textTransform: 'none', fontSize: '13px' }}
                    >
                        {t('common.continueToWeb') || 'Pokračovať na web'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AppBanner;